import { useMemo, useState, type ReactNode } from 'react'
import { Grid, Input, List, Pagination, Space, Table, Typography } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

const { useBreakpoint } = Grid
const { Text } = Typography

// DataTable wraps AntD's Table and gives every CRUD page the same
// baseline UX:
//
//   * Search input above the table — filters across every string
//     field of every row, case-insensitive.
//   * Pagination on by default (25 / page, size-changer enabled).
//   * Sortable columns out of the box — any column with `dataIndex`
//     gets a default sorter inferred from the value type (string →
//     localeCompare, number → subtract). Columns that already
//     define `sorter` keep their custom one.
//
// Drop-in replacement: callers pass the same `columns` + `dataSource`
// props they would to `<Table>`. Search visibility and page-size
// can be customised via the props.
interface DataTableProps<T> extends Omit<TableProps<T>, 'pagination'> {
  pageSize?: number
  searchPlaceholder?: string
  searchable?: boolean
  // toolbar renders to the right of the search input, useful for
  // "Add" buttons. `extraActions` is the proxy-lb-waf-style alias —
  // both work to keep migration painless.
  toolbar?: ReactNode
  extraActions?: ReactNode
  // renderMobileCard switches from a dense Table (desktop) to a
  // List of cards (≤ md breakpoint). Mirrors the old EdgeGuard
  // ProTable mobile mode. Pass undefined to use the default Table
  // also on mobile (with horizontal scroll).
  renderMobileCard?: (record: T, index: number) => ReactNode
  // server switches to server-side paging: dataSource is exactly the current
  // page, `total` the full row count of the (already filtered) result. The
  // built-in client search is hidden because it could only search the loaded
  // page — pass the page's own server filter instead. Column sorters then
  // reorder the current page only.
  server?: {
    current: number
    pageSize: number
    total: number
    onChange: (page: number, pageSize: number) => void
  }
}

function inferSorter<T>(dataIndex: string | string[] | undefined) {
  if (dataIndex == null) return undefined
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex]
  const get = (row: T): unknown => path.reduce<unknown>(
    (acc, k) => (acc != null && typeof acc === 'object' ? (acc as Record<string, unknown>)[k] : undefined),
    row as unknown,
  )
  return (a: T, b: T) => {
    const va = get(a), vb = get(b)
    if (typeof va === 'number' && typeof vb === 'number') return va - vb
    if (typeof va === 'boolean' && typeof vb === 'boolean') return Number(va) - Number(vb)
    const sa = String(va ?? '')
    const sb = String(vb ?? '')
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' })
  }
}

function flatStringValues(row: unknown): string[] {
  if (row == null) return []
  if (typeof row === 'string') return [row]
  if (typeof row === 'number' || typeof row === 'boolean') return [String(row)]
  if (Array.isArray(row)) return row.flatMap(flatStringValues)
  if (typeof row === 'object') {
    return Object.values(row as Record<string, unknown>).flatMap(flatStringValues)
  }
  return []
}

export default function DataTable<T extends object>(
  props: DataTableProps<T>,
) {
  const { t } = useTranslation()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  void isMobile // available for future scroll-x toggles when no card renderer
  const {
    dataSource,
    columns,
    pageSize = 25,
    searchPlaceholder,
    searchable = true,
    toolbar,
    extraActions,
    renderMobileCard,
    server,
    rowKey,
    loading,
    ...rest
  } = props

  const [search, setSearch] = useState('')
  const [localPage, setLocalPage] = useState(1)
  const [localPerPage, setLocalPerPage] = useState(pageSize)
  const page = server ? server.current : localPage
  const perPage = server ? server.pageSize : localPerPage
  const setPaging = (p: number, ps: number) => {
    if (server) server.onChange(p, ps)
    else { setLocalPage(p); setLocalPerPage(ps) }
  }

  const filtered = useMemo(() => {
    if (server || !search || !dataSource) return dataSource
    const q = search.toLowerCase()
    return (dataSource as readonly T[]).filter((row) =>
      flatStringValues(row).some((s) => s.toLowerCase().includes(q)),
    )
  }, [dataSource, search, server])

  const enhancedCols: ColumnsType<T> = useMemo(() => {
    if (!columns) return []
    return (columns as ColumnsType<T>).map((c) => {
      // Skip if user already declared a sorter or this column has
      // no dataIndex (e.g. action columns).
      const col = c as Record<string, unknown>
      if (col.sorter || col.dataIndex == null) return c
      const sorter = inferSorter<T>(col.dataIndex as string | string[])
      if (!sorter) return c
      return { ...c, sorter }
    })
  }, [columns])

  const actions = toolbar ?? extraActions
  const records = (filtered ?? []) as T[]
  const start = (page - 1) * perPage
  const visible = server ? records : records.slice(start, start + perPage)
  const totalRows = server ? server.total : records.length
  const showSearch = searchable && !server

  return (
    <Space direction="vertical" className="w-full mb-12" size="small">
      {(showSearch || actions) && (
        <div className="datatable-toolbar">
          {showSearch && (
            <Input
              prefix={<SearchOutlined />}
              placeholder={searchPlaceholder ?? t('common.search')}
              allowClear
              onChange={(e) => { setSearch(e.target.value); setLocalPage(1) }}
              className="datatable-search"
            />
          )}
          {actions && <Space wrap>{actions}</Space>}
        </div>
      )}

      {isMobile && renderMobileCard ? (
        <>
          <List
            loading={loading}
            dataSource={visible}
            renderItem={(record, idx) => (
              <List.Item className="datatable-list-item">
                {renderMobileCard(record, start + idx)}
              </List.Item>
            )}
            locale={{ emptyText: t('common.noData') }}
          />
          <div className="datatable-pagination">
            <Pagination
              current={page}
              pageSize={perPage}
              total={totalRows}
              onChange={setPaging}
              showSizeChanger
              size="small"
              pageSizeOptions={['20', '50', '100', '200']}
              showTotal={(tot) => <Text type="secondary">{t('common.totalRows', { count: tot })}</Text>}
            />
          </div>
        </>
      ) : (
        <Table<T>
          size="small"
          rowKey={rowKey}
          loading={loading}
          {...rest}
          dataSource={filtered}
          columns={enhancedCols}
          pagination={{
            pageSize: perPage,
            current: page,
            total: server ? server.total : undefined,
            onChange: setPaging,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => t('common.totalRows', { count: total }),
          }}
        />
      )}
    </Space>
  )
}
